import React, { useState, useEffect } from 'react';
import { XMarkIcon, BellIcon, ChatBubbleLeftRightIcon, AtSymbolIcon, ExclamationCircleIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline';

// Notification Settings Modal for chat notifications
export default function NotificationSettingsModal({ isOpen, onClose, settings, onSave }) {
  const [localSettings, setLocalSettings] = useState(settings || {
    newMessages: true,
    privateOnly: false,
    mentions: true,
    urgentQueries: true,
    sound: true,
    desktop: true,
  });

  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const updateSetting = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  if (!isOpen) return null;

  const Toggle = ({ checked, onChange }) => (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
        checked ? 'bg-purple-600' : 'bg-gray-600'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-0'
        }`}
      />
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#0A0F1E] rounded-xl border border-white/10 shadow-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellIcon className="w-5 h-5 text-purple-400" />
            <h2 className="text-base font-semibold text-white">Notification Settings</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          <div className="p-4 space-y-6">
            {/* New Messages */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white font-medium">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-400" />
                <span>New Messages</span>
              </div>
              <div className="space-y-3 pl-7">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm text-white">All messages</p>
                    <p className="text-xs text-gray-500">Get notified for every new message</p>
                  </div>
                  <Toggle
                    checked={localSettings.newMessages}
                    onChange={(checked) => updateSetting('newMessages', checked)}
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm text-white">Private messages only</p>
                    <p className="text-xs text-gray-500">Only notify for direct messages</p>
                  </div>
                  <Toggle
                    checked={localSettings.privateOnly}
                    onChange={(checked) => updateSetting('privateOnly', checked)}
                  />
                </label>
              </div>
            </div>

            {/* Mentions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white font-medium">
                <AtSymbolIcon className="w-5 h-5 text-purple-400" />
                <span>Mentions</span>
              </div>
              <div className="pl-7">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm text-white">When someone mentions you</p>
                    <p className="text-xs text-gray-500">@mentions in chat</p>
                  </div>
                  <Toggle
                    checked={localSettings.mentions}
                    onChange={(checked) => updateSetting('mentions', checked)}
                  />
                </label>
              </div>
            </div>

            {/* Urgent Queries */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white font-medium">
                <ExclamationCircleIcon className="w-5 h-5 text-purple-400" />
                <span>Urgent Queries</span>
              </div>
              <div className="pl-7">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm text-white">AI-detected urgent messages</p>
                    <p className="text-xs text-gray-500">Get alerts for high-priority queries</p>
                  </div>
                  <Toggle
                    checked={localSettings.urgentQueries}
                    onChange={(checked) => updateSetting('urgentQueries', checked)}
                  />
                </label>
              </div>
            </div>

            {/* Sound & Visual */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-white font-medium">
                <SpeakerWaveIcon className="w-5 h-5 text-purple-400" />
                <span>Sound & Visual</span>
              </div>
              <div className="space-y-3 pl-7">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm text-white">Play sound</p>
                    <p className="text-xs text-gray-500">Audio notification for new messages</p>
                  </div>
                  <Toggle
                    checked={localSettings.sound}
                    onChange={(checked) => updateSetting('sound', checked)}
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm text-white">Desktop notifications</p>
                    <p className="text-xs text-gray-500">Browser push notifications</p>
                  </div>
                  <Toggle
                    checked={localSettings.desktop}
                    onChange={(checked) => updateSetting('desktop', checked)}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/10 flex justify-center gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-lg bg-[#6a1fbf] hover:bg-[#7a2fcf] text-white transition-colors text-sm font-medium"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
